import React, { useState, useEffect } from "react";
import {
	TextField,
	Button,
	Typography,
	Paper,
	Container,
	Grid,
	Box,
	Chip,
	LinearProgress,
	Card,
	CardContent,
	CardMedia,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Divider,
	Alert,
	Snackbar,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
} from "@mui/material";
import {
	Description,
	Send,
	Language,
	Wc,
	VideoCall,
	AutoAwesome,
	CheckCircle,
} from "@mui/icons-material";
import workflow from "../images/workflow.gif";

const LanguageOptions = [
	"afrikaans",
	"albanian",
	"amharic",
	"arabic",
	"armenian",
	"assamese",
	"azerbaijani",
	"basque",
	"burmese",
	"bulgarian",
	"bengali",
	"bosnian",
	"catalan",
	"cantonese",
	"chinese",
	"czech",
	"croatian",
	"danish",
	"dutch",
	"english",
	"estonian",
	"finnish",
	"filipino",
	"french",
	"galician",
	"gujarati",
	"german",
	"greek",
	"georgian",
	"hebrew",
	"hindi",
	"hungarian",
	"indonesian",
	"icelandic",
	"irish",
	"italian",
	"inuktitut",
	"japanese",
	"javanese",
	"kazakh",
	"khmer",
	"kannada",
	"korean",
	"lao",
	"lithuanian",
	"latvian",
	"macedonian",
	"malayalam",
	"mongolian",
	"marathi",
	"malay",
	"maltese",
	"norwegian",
	"nepali",
	"odia",
	"punjabi",
	"polish",
	"pashto",
	"persian",
	"portuguese",
	"romanian",
	"russian",
	"sinhalese",
	"slovak",
	"slovenian",
	"somali",
	"spanish",
	"serbian",
	"sundanese",
	"swedish",
	"swahili",
	"tamil",
	"telugu",
	"thai",
	"turkish",
	"ukrainian",
	"urdu",
	"uzbek",
	"vietnamese",
	"welsh",
	"wu chinese",
	"zulu",
];

const TutorialVideo = () => {
	const [formData, setFormData] = useState({
		language: "english",
		gender: "female",
	});
	const [description, setDescription] = useState("Topic");
	const [generatedScript, setGeneratedScript] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [quota, setQuota] = useState(0);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	useEffect(() => {
		getUserInfo();
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const handleDescriptionChange = (e) => {
		setDescription(e.target.value);
	};

	const getUserInfo = async () => {
		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const base_url =
				"https://pmco1162s7.execute-api.us-east-1.amazonaws.com/prod";

			const body = {
				task: "getUserInfo",
			};

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const user_info = await response.json();
			setQuota(user_info[0].quota);
			localStorage.setItem("user_info", JSON.stringify(user_info[0]));
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to fetch user information",
				severity: "error",
			});
		}
	};

	const handleSubmitScript = async () => {
		const num_words = generatedScript.split(" ").length;
		const topic_char_count = description.length;

		if (
			description === "" ||
			topic_char_count > 20 ||
			generatedScript === "" ||
			num_words < 30 ||
			num_words > 170
		) {
			setSnackbar({
				open: true,
				message:
					"Please enter a valid topic and script. Topic should be between 1 and 20 characters. Script should be between 30 and 170 words.",
				severity: "error",
			});
			return;
		}

		setIsLoading(true);

		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const base_url =
				"https://pmco1162s7.execute-api.us-east-1.amazonaws.com/prod";

			const body = {
				task: "submitVideoGenRequest",
				script: generatedScript,
				topic: description,
				language: formData.language,
				gender: formData.gender,
			};

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();

			if (data.code === 200) {
				setSnackbar({
					open: true,
					message:
						"Your script has been submitted. You'll receive an email (usually 5-10 minutes) when the video is ready.",
					severity: "success",
				});
			} else {
				setSnackbar({
					open: true,
					message: "Something went wrong. Please try again later.",
					severity: "error",
				});
			}
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to submit script",
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleScriptChange = (e) => {
		setGeneratedScript(e.target.value);
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	// Calculate word count for display
	const wordCount = generatedScript ? generatedScript.split(" ").length : 0;
	const isWordCountValid = wordCount >= 30 && wordCount <= 170;

	return (
		<Container maxWidth="lg" className="py-8">
			<Paper elevation={3} className="rounded-lg overflow-hidden">
				<Box className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 text-white">
					<Typography variant="h4" className="font-bold text-center">
						Create Avatar Video
					</Typography>
					<Typography
						variant="subtitle1"
						className="text-center mt-2"
					>
						Generate videos with AI avatars in multiple languages
					</Typography>
				</Box>

				<CardContent className="p-6 bg-white">
					<Box className="mb-6">
						<Grid container spacing={2} className="mb-4">
							<Grid item xs={12} md={6}>
								<Chip
									icon={<AutoAwesome />}
									label={`Available Credits: ${quota}/20`}
									color="primary"
									variant="outlined"
									className="px-4 py-3"
								/>
							</Grid>
						</Grid>

						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center"
						>
							<Description className="mr-2" />
							Topic or Video Description
						</Typography>

						<TextField
							fullWidth
							variant="outlined"
							placeholder="Enter a topic or description..."
							value={description}
							onChange={handleDescriptionChange}
							helperText="Keep your topic under 20 characters"
							className="mb-4"
						/>

						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center mt-4"
						>
							<VideoCall className="mr-2" />
							Avatar Settings
						</Typography>

						<Grid container spacing={3} className="my-4">
							<Grid item xs={12} md={6}>
								<FormControl fullWidth variant="outlined">
									<InputLabel id="language-label">
										Language
									</InputLabel>
									<Select
										labelId="language-label"
										id="language"
										name="language"
										value={formData.language}
										onChange={handleChange}
										label="Language"
										startAdornment={
											<Language className="mr-2 text-gray-500" />
										}
									>
										{LanguageOptions.map((lang) => (
											<MenuItem key={lang} value={lang}>
												{lang.charAt(0).toUpperCase() +
													lang.slice(1)}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} md={6}>
								<FormControl fullWidth variant="outlined">
									<InputLabel id="gender-label">
										Gender
									</InputLabel>
									<Select
										labelId="gender-label"
										id="gender"
										name="gender"
										value={formData.gender}
										onChange={handleChange}
										label="Gender"
										startAdornment={
											<Wc className="mr-2 text-gray-500" />
										}
									>
										<MenuItem value="male">Male</MenuItem>
										<MenuItem value="female">
											Female
										</MenuItem>
									</Select>
								</FormControl>
							</Grid>
						</Grid>
					</Box>

					{isLoading && <LinearProgress className="mb-6" />}

					<Divider className="my-4" />

					<Box className="mt-6">
						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center"
						>
							<Description className="mr-2" />
							Script Editor
						</Typography>

						<TextField
							fullWidth
							multiline
							rows={10}
							variant="outlined"
							value={generatedScript}
							onChange={handleScriptChange}
							placeholder="Write your script here..."
							className="mb-4"
						/>

						<Box className="flex justify-between items-center mb-4">
							<Typography
								variant="body2"
								className={`${
									isWordCountValid
										? "text-green-600"
										: "text-red-600"
								}`}
							>
								Word count: {wordCount}/170{" "}
								{!isWordCountValid && "(30-170 words required)"}
							</Typography>

							<Button
								variant="contained"
								color="success"
								onClick={handleSubmitScript}
								startIcon={<Send />}
								disabled={isLoading || !isWordCountValid}
								className="bg-green-600 hover:bg-green-700 font-bold py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all"
							>
								Submit Script for Video
							</Button>
						</Box>
					</Box>

					<Divider className="my-4" />

					<Box className="mt-6">
						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center"
						>
							<VideoCall className="mr-2" />
							Avatar Video Creation Process
						</Typography>

						<Box className="mt-4">
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={12} md={6}>
									<List className="pl-4">
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="primary"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Enter a topic for your avatar video" />
										</ListItem>
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="primary"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Select your preferred language and gender" />
										</ListItem>
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="primary"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Write a script (30-170 words)" />
										</ListItem>
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="primary"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Receive your avatar video within 5-10 minutes via email" />
										</ListItem>
									</List>
								</Grid>
								<Grid item xs={12} md={6}>
									<CardMedia
										component="img"
										image={workflow}
										alt="Avatar video creation workflow"
										className="w-full h-auto object-contain rounded-lg"
										style={{ maxWidth: "100%" }}
									/>
								</Grid>
							</Grid>
						</Box>
					</Box>
				</CardContent>
			</Paper>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default TutorialVideo; 